"use client";

import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Save, Plus, Trash2, Users, Loader2, MessageSquare } from "lucide-react";
import { getCompanyName } from "@/lib/textron-data-processor";
import { Badge } from "../ui/badge";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import { ReviewComment } from "../shared/ReviewComment";

interface ComponentDetail {
  componentName: string;
  materiality: {
    percentage: number;
    balanceOfChosenBenchmark: number;
    determinedAmount: number;
  };
  performanceMateriality: {
    percentage: number;
    balanceOfChosenBenchmark: number;
    determinedAmount: number;
  };
  clearlyTrivialThreshold: {
    percentage: number;
    balanceOfChosenBenchmark: number;
    determinedAmount: number;
  };
}

interface ComponentMaterialityData {
  componentMaterialityRequired: boolean;
  componentDetails: ComponentDetail[];
}

interface ComponentMaterialityProps {
  onDataChange: (data: ComponentMaterialityData) => void;
  initialData: ComponentMaterialityData | null;
  crossSectionAnswers?: Record<string, string | number | boolean>;
  companyName?: string;
  ep1Answer?: string | null;
  loadingEp1?: boolean;
}

export function ComponentMateriality({
  onDataChange,
  initialData,
  crossSectionAnswers = {},
  companyName: propCompanyName,
  ep1Answer: propEp1Answer,
  loadingEp1: propLoadingEp1,
}: ComponentMaterialityProps) {
  const { isArchivalMode } = useArchivalMode();
  const [companyName, setCompanyName] = useState<string>("");
  const [ep1Answer, setEp1Answer] = useState<string | null>(null);
  const [loadingEp1, setLoadingEp1] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [reviewCommentOpen, setReviewCommentOpen] = useState(false);

  const [formData, setFormData] = useState({
    componentMaterialityRequired: false,
    componentDetails: [] as Array<{
      componentName: string;
      materiality: {
        percentage: number;
        balanceOfChosenBenchmark: number;
        determinedAmount: number;
      };
      performanceMateriality: {
        percentage: number;
        balanceOfChosenBenchmark: number;
        determinedAmount: number;
      };
      clearlyTrivialThreshold: {
        percentage: number;
        balanceOfChosenBenchmark: number;
        determinedAmount: number;
      };
    }>,
  });

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      if (propCompanyName) {
        setCompanyName(propCompanyName);
        return;
      }
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");
      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, [propCompanyName]);

  // Use EP-1 from parent (MaterialitySection) if provided, otherwise load it
  useEffect(() => {
    if (propEp1Answer !== undefined) {
      // EP-1 is provided from parent component (MaterialitySection)
      setEp1Answer(propEp1Answer);
      setLoadingEp1(propLoadingEp1 ?? false);
      return;
    }

    // Fallback: Load EP-1 answer from SharePoint if not provided by parent
    const loadEp1FromSharePoint = async () => {
      if (!companyName) {
        setLoadingEp1(false);
        return;
      }

      try {
        const planningAPI = (window as any).planningAPI;
        if (!planningAPI?.loadResponses) {
          console.log("Planning API not available for EP-1 check");
          setLoadingEp1(false);
          return;
        }

        // Load Engagement Acceptance responses from SharePoint
        const sharePointResponses = await planningAPI.loadResponses({
          companyName,
          sectionKey: "engagement-acceptance"
        });

        if (sharePointResponses && sharePointResponses.success && sharePointResponses.data) {
          const responses = sharePointResponses.data.responses || sharePointResponses.data;
          
          // Check for EP-1 in the responses
          if (responses['EP-1'] && responses['EP-1'].selectedOption) {
            setEp1Answer(responses['EP-1'].selectedOption);
            console.log("EP-1 loaded from SharePoint:", responses['EP-1'].selectedOption);
          } else {
            // EP-1 not found or not answered
            setEp1Answer(null);
            console.log("EP-1 not found in SharePoint responses or not answered");
          }
        } else {
          setEp1Answer(null);
        }
      } catch (error) {
        console.error("Failed to load EP-1 from SharePoint:", error);
        setEp1Answer(null);
      } finally {
        setLoadingEp1(false);
      }
    };

    loadEp1FromSharePoint();
  }, [companyName, propEp1Answer, propLoadingEp1]);

  // Load data from SharePoint when component mounts
  useEffect(() => {
    const loadFromSharePoint = async () => {
      if (!companyName) return;

      try {
        const planningAPI = (window as any).planningAPI;
        if (!planningAPI?.loadResponses) {
          console.log("Planning API not available, using initialData");
          if (initialData) {
            setFormData({
              componentMaterialityRequired:
                initialData.componentMaterialityRequired || false,
              componentDetails: (initialData.componentDetails || []).map(
                (detail: ComponentDetail) => ({
                  componentName: detail.componentName || "",
                  materiality: {
                    percentage: detail.materiality?.percentage || 0,
                    balanceOfChosenBenchmark:
                      detail.materiality?.balanceOfChosenBenchmark || 0,
                    determinedAmount:
                      detail.materiality?.determinedAmount || 0,
                  },
                  performanceMateriality: {
                    percentage: detail.performanceMateriality?.percentage || 0,
                    balanceOfChosenBenchmark:
                      detail.performanceMateriality?.balanceOfChosenBenchmark || 0,
                    determinedAmount:
                      detail.performanceMateriality?.determinedAmount || 0,
                  },
                  clearlyTrivialThreshold: {
                    percentage: detail.clearlyTrivialThreshold?.percentage || 0,
                    balanceOfChosenBenchmark:
                      detail.clearlyTrivialThreshold?.balanceOfChosenBenchmark || 0,
                    determinedAmount:
                      detail.clearlyTrivialThreshold?.determinedAmount || 0,
                  },
                }),
              ),
            });
          }
          return;
        }

        const sharePointResponses = await planningAPI.loadResponses({
          companyName,
          sectionKey: "materiality"
        });

        if (sharePointResponses && sharePointResponses.success && sharePointResponses.data) {
          const responses = sharePointResponses.data.responses || sharePointResponses.data;
          console.log("Loaded Component responses from SharePoint:", responses);

          const loadedData: ComponentMaterialityData = {
            componentMaterialityRequired: false,
            componentDetails: []
          };

          // Load M-13 (Component Materiality Required)
          if (responses['M-13'] && responses['M-13'].selectedOption === "Yes") {
            loadedData.componentMaterialityRequired = true;
          }

          // Load Components table
          if (responses['components'] && Array.isArray(responses['components'])) {
            loadedData.componentDetails = responses['components'].map((comp: any) => {
              // Extract data from table rows
              const materialityRow = comp.table?.rows?.find((r: any) => r.particular === "Materiality");
              const performanceRow = comp.table?.rows?.find((r: any) => r.particular === "Performance Materiality");
              const trivialRow = comp.table?.rows?.find((r: any) => r.particular === "Clearly Trivial Threshold");

              return {
                componentName: comp.componentName || "",
                materiality: {
                  percentage: materialityRow?.percentage || 0,
                  balanceOfChosenBenchmark: materialityRow?.balanceOfChosenBenchmark || 0,
                  determinedAmount: materialityRow?.determinedAmount || 0
                },
                performanceMateriality: {
                  percentage: performanceRow?.percentage || 0,
                  balanceOfChosenBenchmark: performanceRow?.balanceOfChosenBenchmark || 0,
                  determinedAmount: performanceRow?.determinedAmount || 0
                },
                clearlyTrivialThreshold: {
                  percentage: trivialRow?.percentage || 0,
                  balanceOfChosenBenchmark: trivialRow?.balanceOfChosenBenchmark || 0,
                  determinedAmount: trivialRow?.determinedAmount || 0
                }
              };
            });
          }

          // If no SharePoint data, fallback to initialData
          if (!loadedData.componentMaterialityRequired && loadedData.componentDetails.length === 0) {
            if (initialData) {
              setFormData({
                componentMaterialityRequired:
                  initialData.componentMaterialityRequired || false,
                componentDetails: (initialData.componentDetails || []).map(
                  (detail: ComponentDetail) => ({
                    componentName: detail.componentName || "",
                    materiality: {
                      percentage: detail.materiality?.percentage || 0,
                      balanceOfChosenBenchmark:
                        detail.materiality?.balanceOfChosenBenchmark || 0,
                      determinedAmount:
                        detail.materiality?.determinedAmount || 0,
                    },
                    performanceMateriality: {
                      percentage: detail.performanceMateriality?.percentage || 0,
                      balanceOfChosenBenchmark:
                        detail.performanceMateriality?.balanceOfChosenBenchmark || 0,
                      determinedAmount:
                        detail.performanceMateriality?.determinedAmount || 0,
                    },
                    clearlyTrivialThreshold: {
                      percentage: detail.clearlyTrivialThreshold?.percentage || 0,
                      balanceOfChosenBenchmark:
                        detail.clearlyTrivialThreshold?.balanceOfChosenBenchmark || 0,
                      determinedAmount:
                        detail.clearlyTrivialThreshold?.determinedAmount || 0,
                    },
                  }),
                ),
              });
              return;
            }
          }

          setFormData(loadedData);
        } else {
          // Fallback to initialData if SharePoint loading fails
          if (initialData) {
            setFormData({
              componentMaterialityRequired:
                initialData.componentMaterialityRequired || false,
              componentDetails: (initialData.componentDetails || []).map(
                (detail: ComponentDetail) => ({
                  componentName: detail.componentName || "",
                  materiality: {
                    percentage: detail.materiality?.percentage || 0,
                    balanceOfChosenBenchmark:
                      detail.materiality?.balanceOfChosenBenchmark || 0,
                    determinedAmount:
                      detail.materiality?.determinedAmount || 0,
                  },
                  performanceMateriality: {
                    percentage: detail.performanceMateriality?.percentage || 0,
                    balanceOfChosenBenchmark:
                      detail.performanceMateriality?.balanceOfChosenBenchmark || 0,
                    determinedAmount:
                      detail.performanceMateriality?.determinedAmount || 0,
                  },
                  clearlyTrivialThreshold: {
                    percentage: detail.clearlyTrivialThreshold?.percentage || 0,
                    balanceOfChosenBenchmark:
                      detail.clearlyTrivialThreshold?.balanceOfChosenBenchmark || 0,
                    determinedAmount:
                      detail.clearlyTrivialThreshold?.determinedAmount || 0,
                  },
                }),
              ),
            });
          }
        }
      } catch (error) {
        console.error("Failed to load Component responses from SharePoint:", error);
        // Fallback to initialData
        if (initialData) {
          setFormData({
            componentMaterialityRequired:
              initialData.componentMaterialityRequired || false,
            componentDetails: (initialData.componentDetails || []).map(
              (detail: ComponentDetail) => ({
                componentName: detail.componentName || "",
                materiality: {
                  percentage: detail.materiality?.percentage || 0,
                  balanceOfChosenBenchmark:
                    detail.materiality?.balanceOfChosenBenchmark || 0,
                  determinedAmount:
                    detail.materiality?.determinedAmount || 0,
                },
                performanceMateriality: {
                  percentage: detail.performanceMateriality?.percentage || 0,
                  balanceOfChosenBenchmark:
                    detail.performanceMateriality?.balanceOfChosenBenchmark || 0,
                  determinedAmount:
                    detail.performanceMateriality?.determinedAmount || 0,
                },
                clearlyTrivialThreshold: {
                  percentage: detail.clearlyTrivialThreshold?.percentage || 0,
                  balanceOfChosenBenchmark:
                    detail.clearlyTrivialThreshold?.balanceOfChosenBenchmark || 0,
                  determinedAmount:
                    detail.clearlyTrivialThreshold?.determinedAmount || 0,
                },
              }),
            ),
          });
        }
      }
    };

    loadFromSharePoint();
  }, [companyName, initialData]);

  const addComponentDetail = () => {
    setFormData((prev) => ({
      ...prev,
      componentDetails: [
        ...prev.componentDetails,
        {
          componentName: "",
          materiality: {
            percentage: 0,
            balanceOfChosenBenchmark: 0,
            determinedAmount: 0,
          },
          performanceMateriality: {
            percentage: 0,
            balanceOfChosenBenchmark: 0,
            determinedAmount: 0,
          },
          clearlyTrivialThreshold: {
            percentage: 0,
            balanceOfChosenBenchmark: 0,
            determinedAmount: 0,
          },
        },
      ],
    }));
  };

  const updateComponentDetail = (
    index: number,
    section: "materiality" | "performanceMateriality" | "clearlyTrivialThreshold",
    field: string,
    value: string | number,
  ) => {
    setFormData((prev) => {
      const newDetails = [...prev.componentDetails];
      newDetails[index] = {
        ...newDetails[index],
        [section]: {
          ...newDetails[index][section],
          [field]: value,
        },
      };

      // Auto-calculate determinedAmount when percentage or balance changes
      if (field === "percentage" || field === "balanceOfChosenBenchmark") {
        const percentage =
          field === "percentage"
            ? Number(value)
            : newDetails[index][section].percentage;
        const balance =
          field === "balanceOfChosenBenchmark"
            ? Number(value)
            : newDetails[index][section].balanceOfChosenBenchmark;
        newDetails[index][section].determinedAmount =
          (percentage * balance) / 100;
      }

      return { ...prev, componentDetails: newDetails };
    });
  };

  const updateComponentName = (index: number, value: string) => {
    setFormData((prev) => {
      const newDetails = [...prev.componentDetails];
      newDetails[index] = { ...newDetails[index], componentName: value };
      return { ...prev, componentDetails: newDetails };
    });
  };

  const removeComponentDetail = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      componentDetails: prev.componentDetails.filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    // Call the original onDataChange for local state management
    onDataChange(formData);
    
    // Save to SharePoint
    if (!companyName) {
      setSaveError("Company name not available");
      setSaveStatus('error');
      return;
    }

    setSaving(true);
    setSaveStatus('saving');
    setSaveError(null);

    try {
      const responses: Record<string, any> = {};

      // M-13: Component Materiality Required (checkbox)
      if (formData.componentMaterialityRequired) {
        responses['M-13'] = {
          questionId: 'M-13',
          selectedOption: "Yes",
          textResponse: null,
          documents: [],
          lastUpdated: new Date().toISOString(),
          companyName
        };
      }

      // Components table - save in the specified format
      if (formData.componentDetails && formData.componentDetails.length > 0) {
        responses['components'] = formData.componentDetails.map((component, index) => ({
          id: `component${index + 1}`,
          componentName: component.componentName || "",
          table: {
            rows: [
              {
                particular: "Materiality",
                percentage: component.materiality?.percentage || null,
                balanceOfChosenBenchmark: component.materiality?.balanceOfChosenBenchmark || null,
                determinedAmount: component.materiality?.determinedAmount || null
              },
              {
                particular: "Performance Materiality",
                percentage: component.performanceMateriality?.percentage || null,
                balanceOfChosenBenchmark: component.performanceMateriality?.balanceOfChosenBenchmark || null,
                determinedAmount: component.performanceMateriality?.determinedAmount || null
              },
              {
                particular: "Clearly Trivial Threshold",
                percentage: component.clearlyTrivialThreshold?.percentage || null,
                balanceOfChosenBenchmark: component.clearlyTrivialThreshold?.balanceOfChosenBenchmark || null,
                determinedAmount: component.clearlyTrivialThreshold?.determinedAmount || null
              }
            ],
            columns: [
              { key: "particular", label: "Particulars" },
              { key: "percentage", label: "Percentage (A)" },
              { key: "balanceOfChosenBenchmark", label: "Balance of Chosen Benchmark (B)" },
              { key: "determinedAmount", label: "Determined Amount" }
            ]
          }
        }));
      }

      // Save all responses to SharePoint
      if (Object.keys(responses).length > 0) {
        const planningAPI = (window as any).planningAPI;
        if (!planningAPI) {
          throw new Error("Planning API not available");
        }

        const result = await planningAPI.saveAllResponses({
          companyName,
          sectionKey: "materiality",
          responses
        });

        if (result.success) {
          setSaveStatus('saved');
          setTimeout(() => setSaveStatus('idle'), 3000);
        } else {
          throw new Error(result.error || "Failed to save responses");
        }
      } else {
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 3000);
      }
    } catch (error) {
      console.error("Failed to save Component responses:", error);
      setSaveError(error instanceof Error ? error.message : "Unknown error");
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  // Check conditional rendering based on EP-1 from SharePoint
  // Priority: SharePoint EP-1 > crossSectionAnswers EP-1
  const ep1Value = ep1Answer !== null ? ep1Answer : (crossSectionAnswers["EP-1"] as string);
  const shouldShowComponent = ep1Value === "Yes";

  // If loading, show loading state
  if (loadingEp1) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-6">
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-600"></div>
          <p className="text-sm text-gray-600">Loading component materiality settings...</p>
        </div>
      </div>
    );
  }

  // If condition is not met, show informational message
  if (!shouldShowComponent) {
    return (
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-6">
        <div className="flex items-center gap-3">
          <div className="rounded-full bg-blue-100 p-2">
            <Users className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-medium text-blue-900">
              Component Materiality Not Applicable
            </h3>
            <p className="text-sm text-blue-700 mt-1">
              Component Materiality is only applicable for group audits. To activate this section, kindly edit response to the question "Is this an audit of group financial statements?" in Engagement Acceptance & Continuance.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Review Comment Button */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-white">Component Materiality</h2>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setReviewCommentOpen(true)}
          className="text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
          title="Review Comment"
        >
          <MessageSquare className="h-4 w-4 mr-2" />
          Review Comment
        </Button>
      </div>
      
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="componentMaterialityRequired"
            checked={formData.componentMaterialityRequired}
            onCheckedChange={(checked) =>
              setFormData((prev) => ({
                ...prev,
                componentMaterialityRequired: checked as boolean,
              }))
            }
          />
          <Label htmlFor="componentMaterialityRequired">
            Component materiality required (for group audits)
          </Label>
        </div>

        {formData.componentMaterialityRequired && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium">Component Details</h4>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addComponentDetail}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Component
              </Button>
            </div>

            {/* Component Cards */}
            {formData.componentDetails.map((component, index) => (
              <div key={index} className="borderp-6 rounded-lg shadow-sm">
                <div className="space-y-4">
                  {/* Component Header */}
                  <div className="flex items-center justify-between border-b pb-3">
                    <div className="space-y-2">
                      <Label
                        htmlFor={`component-name-${index}`}
                        className="text-lg font-medium"
                      >
                        Component {index + 1}
                      </Label>
                      <Input
                        id={`component-name-${index}`}
                        value={component.componentName}
                        onChange={(e) =>
                          updateComponentName(index, e.target.value)
                        }
                        placeholder="Enter component name"
                        className="max-w-md"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeComponentDetail(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Component Table */}
                  <div className="overflow-hidden rounded-lg border">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b">
                          <th className="border-r p-3 text-left text-sm font-medium">
                            Particulars
                          </th>
                          <th className="border-r p-3 text-center text-sm font-medium">
                            Percentage (A)
                          </th>
                          <th className="border-r p-3 text-center text-sm font-medium">
                            Balance of Chosen Benchmark (B)
                          </th>
                          <th className="p-3 text-center text-sm font-medium">
                            Determined Amount
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Materiality Row */}
                        <tr className="border-b">
                          <td className="border-r p-3 text-sm font-medium">
                            Materiality
                          </td>
                          <td className="border-r p-3">
                            <Input
                              type="number"
                              value={
                                component.materiality?.percentage || ""
                              }
                              onChange={(e) =>
                                updateComponentDetail(
                                  index,
                                  "materiality",
                                  "percentage",
                                  Number.parseFloat(e.target.value) || 0,
                                )
                              }
                              placeholder="Enter Percentage"
                              className="text-center"
                              step="0.1"
                            />
                          </td>
                          <td className="border-r p-3">
                            <Input
                              type="number"
                              value={
                                component.materiality?.balanceOfChosenBenchmark || ""
                              }
                              onChange={(e) =>
                                updateComponentDetail(
                                  index,
                                  "materiality",
                                  "balanceOfChosenBenchmark",
                                  Number.parseFloat(e.target.value) || 0,
                                )
                              }
                              placeholder="Enter Amount"
                              className="text-center"
                            />
                          </td>
                          <td className="p-3">
                            <Input
                              type="number"
                              value={
                                component.materiality?.determinedAmount || ""
                              }
                              onChange={(e) =>
                                updateComponentDetail(
                                  index,
                                  "materiality",
                                  "determinedAmount",
                                  Number.parseFloat(e.target.value) || 0,
                                )
                              }
                              placeholder="Product"
                              className="text-center"
                            />
                          </td>
                        </tr>

                        {/* Performance Materiality Row */}
                        <tr className="border-b">
                          <td className="border-r p-3 text-sm font-medium">
                            Performance Materiality
                          </td>
                          <td className="border-r p-3">
                            <Input
                              type="number"
                              value={
                                component.performanceMateriality.percentage ||
                                ""
                              }
                              onChange={(e) =>
                                updateComponentDetail(
                                  index,
                                  "performanceMateriality",
                                  "percentage",
                                  Number.parseFloat(e.target.value) || 0,
                                )
                              }
                              placeholder="Enter Percentage"
                              className="text-center"
                              step="0.1"
                            />
                          </td>
                          <td className="border-r p-3">
                            <Input
                              type="number"
                              value={
                                component.performanceMateriality
                                  .balanceOfChosenBenchmark || ""
                              }
                              onChange={(e) =>
                                updateComponentDetail(
                                  index,
                                  "performanceMateriality",
                                  "balanceOfChosenBenchmark",
                                  Number.parseFloat(e.target.value) || 0,
                                )
                              }
                              placeholder="Enter Amount"
                              className="text-center"
                            />
                          </td>
                          <td className="p-3">
                            <Input
                              type="number"
                              value={
                                component.performanceMateriality
                                  .determinedAmount || ""
                              }
                              onChange={(e) =>
                                updateComponentDetail(
                                  index,
                                  "performanceMateriality",
                                  "determinedAmount",
                                  Number.parseFloat(e.target.value) || 0,
                                )
                              }
                              placeholder="Product"
                              className="text-center"
                            />
                          </td>
                        </tr>

                        {/* Clearly Trivial Threshold Row */}
                        <tr>
                          <td className="border-r p-3 text-sm font-medium">
                            Clearly Trivial Threshold
                          </td>
                          <td className="border-r p-3">
                            <Input
                              type="number"
                              value={
                                component.clearlyTrivialThreshold.percentage ||
                                ""
                              }
                              onChange={(e) =>
                                updateComponentDetail(
                                  index,
                                  "clearlyTrivialThreshold",
                                  "percentage",
                                  Number.parseFloat(e.target.value) || 0,
                                )
                              }
                              placeholder="Enter Percentage"
                              className="text-center"
                              step="0.1"
                            />
                          </td>
                          <td className="border-r p-3">
                            <Input
                              type="number"
                              value={
                                component.clearlyTrivialThreshold
                                  .balanceOfChosenBenchmark || ""
                              }
                              onChange={(e) =>
                                updateComponentDetail(
                                  index,
                                  "clearlyTrivialThreshold",
                                  "balanceOfChosenBenchmark",
                                  Number.parseFloat(e.target.value) || 0,
                                )
                              }
                              placeholder="Enter Amount"
                              className="text-center"
                            />
                          </td>
                          <td className="p-3">
                            <Input
                              type="number"
                              value={
                                component.clearlyTrivialThreshold
                                  .determinedAmount || ""
                              }
                              onChange={(e) =>
                                updateComponentDetail(
                                  index,
                                  "clearlyTrivialThreshold",
                                  "determinedAmount",
                                  Number.parseFloat(e.target.value) || 0,
                                )
                              }
                              placeholder="Product"
                              className="text-center"
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end items-center gap-4 pt-6">
        {saveStatus === 'saved' && (
          <Badge variant="outline" className="border-green-600 text-green-600">
            Saved successfully
          </Badge>
        )}
        {saveStatus === 'error' && saveError && (
          <Badge variant="destructive" className="text-red-600">
            Error: {saveError}
          </Badge>
        )}
        <Button 
          onClick={handleSave} 
          className="flex items-center gap-2"
          disabled={isArchivalMode || saving || !companyName}
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              Save Component Data
            </>
          )}
        </Button>
      </div>
      
      {/* Review Comment Modal */}
      <ReviewComment
        questionId="Materiality-Component"
        questionText="Component Materiality"
        isOpen={reviewCommentOpen}
        onClose={() => setReviewCommentOpen(false)}
        workspace="Materiality"
        subPhase="Component Materiality"
      />
    </div>
  );
}
